/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { BarChart3, LayoutDashboard, TriangleAlert, type LucideIcon } from 'lucide-react';
import { NavLink } from 'react-router';

/* Import context functions */
import { useI18n } from '@/context/I18nContext';

/* Import configuration */
import { APP_ROUTES } from '@/config/appRoutes';

interface ExampleDashboardConfig {
    description: string;
    icon: LucideIcon;
    label: string;
    route: string;
}

const EXAMPLE_DASHBOARDS: ExampleDashboardConfig[] = [
    {
        description: 'Example overview dashboard with combined KPI cards and responsive grid behavior.',
        icon: LayoutDashboard,
        label: 'Overview',
        route: APP_ROUTES.overview,
    },
    {
        description: 'Example statistics dashboard with chart-focused panels and responsive grid behavior.',
        icon: BarChart3,
        label: 'Statistics',
        route: APP_ROUTES.statistics,
    },
    {
        description: 'Example dashboard for visualizing thermal conflicts and related metrics.',
        icon: TriangleAlert,
        label: 'Thermal Conflict',
        route: APP_ROUTES.thermalconflict,
    },
];

function ExampleDashboardsTab() {
    const { t } = useI18n();

    return (
        <div className="flex flex-col gap-6" aria-label={t('Example Dashboards')}>
            <section className="flex flex-col gap-2">
                <h2>{t('Example Dashboards')}</h2>
                <p>{t('Select an example dashboard to open its responsive dashboard layout:')}</p>
            </section>
            <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                {EXAMPLE_DASHBOARDS.map((dashboard) => {
                    const Icon = dashboard.icon;

                    return (
                        <NavLink
                            key={dashboard.route}
                            to={dashboard.route}
                            className="dashboard-panel interactive-surface !min-h-44 no-underline transition-[background,transform] duration-150"
                        >
                            <div className="flex h-full w-full flex-col items-start justify-center gap-3 px-6 text-left">
                                <div className="flex w-full items-start justify-between gap-3">
                                    <div className="flex items-start gap-3">
                                        <Icon className="mt-0.5 h-5 w-5 shrink-0 text-base-content/75" aria-hidden="true" />
                                        <span className="text-xl font-semibold">{t(dashboard.label)}</span>
                                    </div>
                                    <span className="badge badge-primary shrink-0">{t('Demo')}</span>
                                </div>
                                <span className="text-sm text-base-content/70">
                                    {t(dashboard.description)}
                                </span>
                            </div>
                        </NavLink>
                    );
                })}
            </section>
        </div>
    );
}

export default ExampleDashboardsTab;
